import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import authService, { User } from '../services/authService';
import permissionService from '../services/permissionService';

interface RoleSelectorProps {
  className?: string;
  onRoleChange?: (role: string) => void;
}

/**
 * RoleSelector Component
 * 
 * Allows users with multiple roles to switch between them
 * Updates permissions and UI based on selected role
 */
const RoleSelector: React.FC<RoleSelectorProps> = ({ className = '', onRoleChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [currentRole, setCurrentRole] = useState<string>('');
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [isSwitching, setIsSwitching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Role display names
  const getRoleDisplayName = (role: string): string => {
    const roleNames: Record<string, string> = {
      'project_owner': 'Project Owner',
      'bu_head': 'BU Head',
      'admin': 'Admin',
      'operations': 'Operations',
      'accounts': 'Accounts',
    };
    return roleNames[role] || role;
  };

  // Role colors for badges
  const getRoleColor = (role: string): string => {
    const roleColors: Record<string, string> = {
      'project_owner': 'bg-blue-100 text-blue-800 border-blue-300',
      'bu_head': 'bg-purple-100 text-purple-800 border-purple-300',
      'admin': 'bg-green-100 text-green-800 border-green-300',
      'operations': 'bg-green-100 text-green-800 border-green-300',
      'accounts': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    };
    return roleColors[role] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  useEffect(() => {
    // Get user data
    const userData = authService.getUserData();
    if (userData) {
      setUser(userData);
      setCurrentRole(userData.role || '');
      // Use pbsRoles instead of available_roles
      setAvailableRoles(userData.pbsRoles || [userData.role]);
    }

    // Subscribe to auth state changes
    const unsubscribe = authService.onAuthStateChange((isAuthenticated, userData) => {
      if (isAuthenticated && userData) {
        setUser(userData);
        setCurrentRole(userData.role || '');
        // Use pbsRoles instead of available_roles
        setAvailableRoles(userData.pbsRoles || [userData.role]);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleRoleSwitch = async (newRole: string) => {
    if (newRole === currentRole || isSwitching) {
      return;
    }

    setIsSwitching(true);
    setShowDropdown(false);

    try {
      await authService.switchRole(newRole);
      setCurrentRole(newRole);

      // Update user data in state
      if (user) {
        const updatedUser = { ...user, role: newRole };
        setUser(updatedUser);
      }

      // Reload permissions
      await permissionService.initializePermissions();

      // Determine redirect based on new role
      let redirectPath: string | null = null;
      
      if (newRole === 'admin') {
        // Redirect to admin page when admin role is selected
        redirectPath = '/admin/salaries';
      } else if (newRole === 'project_owner') {
        // Redirect to dashboard when project_owner role is selected
        redirectPath = '/dashboard';
      }

      // Notify parent component
      if (onRoleChange) {
        onRoleChange(newRole);
      }

      // Redirect after role change
      if (redirectPath) {
        // Use window.location for full page navigation that works with reload
        window.location.href = redirectPath;
      }

      // Show success message
      console.log(`Switched to ${getRoleDisplayName(newRole)} role`);
    } catch (error) {
      console.error('Failed to switch role:', error);
      alert('Failed to switch role. Please try again.');
    } finally {
      setIsSwitching(false);
    }
  };

  // Don't show selector if user has only one role
  if (availableRoles.length <= 1) {
    return (
      <div className={`inline-flex items-center ${className}`}>
        <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${getRoleColor(currentRole)}`}>
          {getRoleDisplayName(currentRole)}
        </span>
      </div>
    );
  }

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Current Role Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isSwitching}
        aria-label={`Current role: ${getRoleDisplayName(currentRole)}. Click to switch role.`}
        aria-expanded={showDropdown}
        aria-haspopup="true"
        className={`
          flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium border
          ${getRoleColor(currentRole)}
          hover:opacity-80 transition-opacity
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
        `}
      >
        <span>{getRoleDisplayName(currentRole)}</span>
        <svg
          className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {showDropdown && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />

          {/* Dropdown List */}
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" role="menu" aria-label="Available roles">
            <div className="px-3 py-2 border-b border-gray-200">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">
                Switch Role
              </p>
            </div>

            {availableRoles.map((role) => (
              <button
                key={role}
                onClick={() => handleRoleSwitch(role)}
                disabled={role === currentRole || isSwitching}
                role="menuitem"
                aria-label={`Switch to ${getRoleDisplayName(role)} role`}
                aria-disabled={role === currentRole || isSwitching}
                className={`
                  w-full text-left px-3 py-2 text-sm
                  hover:bg-gray-50 transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-between
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset
                  ${role === currentRole ? 'bg-blue-50 font-medium' : ''}
                `}
              >
                <span className="flex-1">{getRoleDisplayName(role)}</span>
                {role === currentRole && (
                  <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}

            <div className="px-3 py-2 border-t border-gray-200 mt-1">
              <p className="text-xs text-gray-500">
                Your permissions will update based on the selected role.
              </p>
            </div>
          </div>
        </>
      )}

      {/* Loading Overlay */}
      {isSwitching && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-full" role="status" aria-live="polite">
          <svg
            className="animate-spin h-4 w-4 text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span className="sr-only">Switching role, please wait</span>
        </div>
      )}
    </div>
  );
};

export default RoleSelector;


